import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  BookOpen,
  Loader2,
  Sparkles,
  Video,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import {
  completeSession,
  fmtDateTime,
  joinSession,
  startSession,
  statusBadge,
  typeBadge,
} from "@/lib/schedule";
import { getCourse } from "@/lib/courses";
import { useAuth } from "@/context/AuthContext";
import AttendancePanel from "@/components/AttendancePanel";

const JITSI_SCRIPT = "https://meet.jit.si/external_api.js";

function ensureJitsiScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
    const existing = document.querySelector(`script[src="${JITSI_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () =>
        resolve(window.JitsiMeetExternalAPI)
      );
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = JITSI_SCRIPT;
    script.async = true;
    script.onload = () => resolve(window.JitsiMeetExternalAPI);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function LiveSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [joinData, setJoinData] = useState(null);
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  // 1. Fetch join payload
  useEffect(() => {
    let alive = true;
    joinSession(id)
      .then((data) => {
        if (!alive) return;
        setJoinData(data);
      })
      .catch((err) => {
        if (!alive) return;
        const detail = err?.response?.data?.detail;
        setError(
          detail === "course_not_published"
            ? "This course isn't published yet."
            : detail === "payment_required"
              ? "Complete your payment to join live classes for this course."
              : detail === "not_enrolled"
                ? "You aren't enrolled in this course."
                : detail === "not_authorized"
                  ? "You don't have access to this live class."
                  : detail || "Could not load this session."
        );
      });
    return () => {
      alive = false;
    };
  }, [id]);

  // 2. Once joinData is ready, fetch course (for chapter notes) — best-effort.
  useEffect(() => {
    if (!joinData) return;
    const courseId = joinData.session?.course_id;
    if (!courseId) return;
    getCourse(courseId)
      .then(setCourse)
      .catch(() => setCourse(null));
  }, [joinData]);

  // 3. Mount Jitsi
  useEffect(() => {
    if (!joinData || !containerRef.current) return;
    let api;
    let cancelled = false;
    ensureJitsiScript()
      .then((JitsiAPI) => {
        if (cancelled || !containerRef.current) return;
        api = new JitsiAPI("meet.jit.si", {
          roomName: joinData.jitsi_room,
          parentNode: containerRef.current,
          width: "100%",
          height: 600,
          userInfo: { displayName: joinData.display_name || user?.name || "Guest" },
          configOverwrite: {
            prejoinPageEnabled: false,
            startWithAudioMuted: true,
            disableDeepLinking: true,
          },
          interfaceConfigOverwrite: {
            DEFAULT_REMOTE_DISPLAY_NAME: "Student",
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
          },
        });
        apiRef.current = api;
        api.addListener("videoConferenceLeft", () => {
          toast.message("Live class ended");
          navigate("/calendar");
        });
        api.addListener("readyToClose", () => {
          navigate("/calendar");
        });
      })
      .catch(() => setError("Failed to load Jitsi. Check your network."));
    return () => {
      cancelled = true;
      try {
        api?.dispose();
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinData]);

  const onStart = async () => {
    setBusy(true);
    try {
      const fresh = await startSession(id);
      setJoinData((d) => ({ ...d, session: fresh }));
      toast.success("Session marked as live");
    } catch {
      toast.error("Couldn't update status");
    } finally {
      setBusy(false);
    }
  };

  const onComplete = async () => {
    setBusy(true);
    try {
      await completeSession(id);
      toast.success("Session marked complete");
      try {
        apiRef.current?.executeCommand("hangup");
      } catch {
        /* ignore */
      }
      navigate("/calendar");
    } catch {
      toast.error("Couldn't update status");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 text-stone-900">
        <DashboardHeader subtitle="Live class" />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <Card className="border-red-200">
            <CardContent className="py-12 text-center space-y-3">
              <div
                className="text-sm font-medium text-red-700"
                data-testid="live-error"
              >
                {error}
              </div>
              <Button variant="outline" onClick={() => navigate("/calendar")}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to calendar
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (!joinData) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-stone-700" />
      </div>
    );
  }

  const session = joinData.session;
  const linkedChapter =
    course?.chapters?.find((c) => c.id === session.linked_chapter_id) || null;

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="live-session-page"
    >
      <DashboardHeader subtitle="Live class" />
      <main className="mx-auto max-w-7xl px-6 py-6 space-y-4">
        <BackButton to="/calendar" label="Calendar" testId="back-to-calendar" />
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5 text-amber-600" />
              <h1
                className="text-xl font-semibold tracking-tight"
                data-testid="live-title"
              >
                {session.title}
              </h1>
            </div>
            <div className="text-xs text-stone-500 mt-1">
              {fmtDateTime(session.starts_at)}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className={typeBadge(session.session_type)}>
                {session.session_type}
              </Badge>
              <Badge variant="outline" className={statusBadge(session.status)}>
                {session.status}
              </Badge>
              <Badge variant="outline" data-testid="live-role">
                {joinData.role}
              </Badge>
            </div>
          </div>
          {joinData.can_moderate && (
            <div className="flex items-center gap-2">
              {session.status !== "live" && session.status !== "completed" && (
                <Button
                  variant="outline"
                  onClick={onStart}
                  disabled={busy}
                  data-testid="moderator-start"
                >
                  <Sparkles className="mr-1 h-4 w-4" /> Mark live
                </Button>
              )}
              {session.status !== "completed" && (
                <Button
                  variant="outline"
                  onClick={onComplete}
                  disabled={busy}
                  className="border-emerald-300 text-emerald-700"
                  data-testid="moderator-complete"
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" /> End class
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
          <Card className="border-stone-200 overflow-hidden">
            <CardContent className="p-0">
              {session.external_link ? (
                <div className="p-6 space-y-3" data-testid="external-link-block">
                  <p className="text-sm text-stone-700">
                    This session uses an external link instead of the built-in
                    Jitsi room.
                  </p>
                  <a
                    href={session.external_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-stone-900 underline"
                  >
                    <ExternalLink className="h-4 w-4" /> {session.external_link}
                  </a>
                </div>
              ) : (
                <div
                  ref={containerRef}
                  className="bg-stone-900"
                  style={{ minHeight: 600 }}
                  data-testid="jitsi-iframe-container"
                />
              )}
            </CardContent>
          </Card>

          <Card className="border-stone-200" data-testid="chapter-sidebar">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-amber-600" /> Linked chapter
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {linkedChapter ? (
                <div className="space-y-3" data-testid="linked-chapter">
                  <div className="font-medium">{linkedChapter.title}</div>
                  {linkedChapter.learning_objectives?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-stone-700 mb-1">
                        Learning objectives
                      </div>
                      <ul className="list-disc pl-5 text-xs space-y-1 text-stone-700">
                        {linkedChapter.learning_objectives.map((o, i) => (
                          <li key={i}>{o}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {linkedChapter.classwork?.lesson_notes_markdown && (
                    <div>
                      <div className="text-xs font-semibold text-stone-700 mb-1">
                        Notes
                      </div>
                      <pre className="whitespace-pre-wrap text-xs text-stone-700 bg-stone-50 p-2 rounded border border-stone-200 max-h-[360px] overflow-auto">
                        {linkedChapter.classwork.lesson_notes_markdown}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-stone-500" data-testid="no-linked-chapter">
                  No chapter linked. Teachers can link a chapter when adding an
                  ad-hoc session for quick reference during the call.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {joinData.can_moderate && (
          <div className="mt-4">
            <AttendancePanel
              sessionId={session.id}
              onMarked={() => {}}
            />
          </div>
        )}
      </main>
    </div>
  );
}
